import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/sidebar/Sidebar';
import Home from './pages/home/Home';
import Integrations from './pages/integrations/Integrations';
import Dashboard from './pages/dashboard/Dashboard';
import Upload from './pages/upload/Upload';
import Alerts from './pages/alerts/Alerts';
import VehicleDetails from './pages/vehicleDetails/VehicleDetails';
import Workflows from './pages/workflows/Workflows';
import CustomerSupport from './pages/customerSupport/CustomerSupport';
import Settings from './pages/settings/Settings';
import RawPicture from './pages/rawpictures/RawPicture';
import Contact from './pages/contact/Contact';
import About from './pages/about/About';
import PrivacyPolicy from './pages/privacy/PrivacyPolicy';
import Login from './pages/login/Login';
import Investors from './pages/investors/Investors';
import FinanceImports from './pages/financeImports/FinanceImports';
import OwnerPortal from './pages/ownerPortal/OwnerPortal';
import VehicleManagement from './pages/vehicleManagement/VehicleManagement';
import VehicleCosts from './pages/vehicleCosts/VehicleCosts';
import './App.css';



// Protected Route component
export function ProtectedRoute({ children, requireAdmin = false, requireInvestor = false, allowedRoles = ['admin', 'investor'] }) {
  const { isAdmin, isInvestor, isLoading, user, getDefaultRoute } = useAuth();
  
  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role) || (requireAdmin && !isAdmin()) || (requireInvestor && !isInvestor())) {
    return <Navigate to={getDefaultRoute()} replace />;
  }
  
  return children;
}

// Layout component that includes sidebar and main content
export function Layout() {
  const location = useLocation();
  const currentPage = location.pathname.substring(1) || 'home';
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const { sessionId, user } = useAuth();

  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  // Prevent body scroll when drawer is open on mobile
  React.useEffect(() => {
    if (isDrawerOpen && window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  return (
    <div className="dashboard-container">
      {/* Hamburger Menu Button - Only visible on mobile */}
      <button className="hamburger-btn" onClick={handleDrawerToggle} aria-label="Toggle navigation" aria-expanded={isDrawerOpen}>
        <span className="hamburger-icon">☰</span>
      </button>

      {/* Sidebar */}
      <Sidebar 
        currentPage={currentPage} 
        isDrawerOpen={isDrawerOpen}
        onDrawerClose={handleDrawerClose}
      />

      {/* Main Content */}
      <div className="main-content" key={`${sessionId}-${user?.username || 'guest'}-${user?.role || ''}`}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/owner" element={
            <ProtectedRoute>
              <OwnerPortal />
            </ProtectedRoute>
          } />
          <Route path="/owner/vehicles/:vehicleId" element={
            <ProtectedRoute>
              <OwnerPortal />
            </ProtectedRoute>
          } />
          <Route path="/owner/cars" element={<ProtectedRoute><OwnerPortal /></ProtectedRoute>} />
          <Route path="/owner/cars/:vehicleId" element={<ProtectedRoute><OwnerPortal /></ProtectedRoute>} />
          <Route path="/owner/reports" element={<ProtectedRoute><OwnerPortal /></ProtectedRoute>} />
          <Route path="/owner/profile" element={<ProtectedRoute requireInvestor><OwnerPortal /></ProtectedRoute>} />
          
          {/* Admin-only routes */}
          <Route path="/integrations" element={
            <ProtectedRoute requireAdmin={true}>
              <Integrations />
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/vehicle-details/:id" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']}>
              <VehicleDetails />
            </ProtectedRoute>
          } />
          <Route path="/upload" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']}>
              <Upload />
            </ProtectedRoute>
          } />
          <Route path="/alerts" element={
            <ProtectedRoute requireAdmin={true}>
              <Alerts />
            </ProtectedRoute>
          } />
          <Route path="/workflows" element={
            <ProtectedRoute requireAdmin={true}>
              <Workflows />
            </ProtectedRoute>
          } />
          <Route path="/customer-support" element={
            <ProtectedRoute requireAdmin={true}>
              <CustomerSupport />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute requireAdmin={true}>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/raw-pictures" element={
            <ProtectedRoute allowedRoles={['admin', 'worker']}>
              <RawPicture />
            </ProtectedRoute>
          } />
          <Route path="/admin/investors" element={
            <ProtectedRoute requireAdmin={true}>
              <Investors />
            </ProtectedRoute>
          } />
          <Route path="/admin/investors/:investorId" element={<ProtectedRoute requireAdmin><Investors /></ProtectedRoute>} />
          <Route path="/admin/vehicles" element={<ProtectedRoute requireAdmin><VehicleManagement /></ProtectedRoute>} />
          <Route path="/admin/vehicles/:profileId" element={<ProtectedRoute requireAdmin><VehicleManagement /></ProtectedRoute>} />
          <Route path="/worker/vehicles" element={<ProtectedRoute allowedRoles={['admin', 'worker']}><VehicleCosts /></ProtectedRoute>} />
          <Route path="/worker/vehicles/:profileId/costs" element={<ProtectedRoute allowedRoles={['admin', 'worker']}><VehicleCosts /></ProtectedRoute>} />
          <Route path="/admin/finance" element={
            <ProtectedRoute requireAdmin={true}>
              <FinanceImports />
            </ProtectedRoute>
          } />
          <Route path="/investors" element={<Navigate to="/admin/investors" replace />} />
          <Route path="/finance-imports" element={<Navigate to="/admin/finance" replace />} />
          <Route path="/admin/owner" element={<Navigate to={`/owner${location.search}`} replace />} />
          <Route path="*" element={<div className="portal"><h1>Page not found</h1></div>} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Layout />
      </Router>
    </AuthProvider>
  );
}

export default App;
